// import formatCurrency from 'format-currency'
import moment from 'moment'
import momentTz from 'moment-timezone'
import store from '@/store/index'
import sanitizeHtml from 'sanitize-html'

export function stripHtmlFromInput(html) {
  return sanitizeHtml(html, {
    transformTags: {
      a: sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' })
    }
  })
}

export function numberFormat(number, dec, dsep, tsep) {
  if (isNaN(number) || number == null) return ''

  number = parseFloat(number).toFixed(~~dec)
  tsep = typeof tsep === 'string' ? tsep : ','

  let parts = number.split('.')
  let fnums = parts[0]
  let decimals = parts[1] ? (dsep || '.') + parts[1] : ''

  return fnums.replace(/(\d)(?=(?:\d{3})+$)/g, '$1' + tsep) + decimals
}

export function convertToNumber(value, decimals = 2) {
  if (!value) return
  // select only the numbers
  value = value.replace(/[^0-9]+/g, '')

  // add the dot for the decimals
  value = [value.slice(0, value.length - decimals), '.', value.slice(value.length - decimals)].join('')

  // convert to a number
  return Number(value)
}

export function formatPrice(
  value,
  decimals = 2,
  inCell = false,
  // eslint-disable-next-line no-unused-vars
  locale = store.state.settings.locale,
  decimalSeparator = store.state.settings.decimal_separator,
  thousandsSeparator = store.state.settings.thousand_separator
) {
  if (value === null || typeof value === 'undefined') return

  let str = inCell ? `${value}` : value

  thousandsSeparator = thousandsSeparator === 'space' ? ' ' : thousandsSeparator

  return numberFormat(str, decimals, decimalSeparator, thousandsSeparator)
}

export function formatInputNumber(numberString, outputDecimals = 2, decimalSeparator, thousandsSeparator) {
  if (!numberString) {
    return
  }
  thousandsSeparator = thousandsSeparator === 'space' ? ' ' : thousandsSeparator
  let inputDecimals = numberString.slice(numberString.lastIndexOf(decimalSeparator), numberString.length - 1).length
  let numFromString = null

  if (decimalSeparator === '.') {
    numFromString = convertToNumber(numberString, inputDecimals)
  } else {
    numberString = numberString.replace(/[^0-9]+/g, '')
    let point = numberString.length - inputDecimals
    numberString =
      point < numberString.length
        ? [numberString.slice(0, point), '.', numberString.slice(point)].join('')
        : numberString
    numFromString = Number(numberString)
  }
  return numberFormat(numFromString, outputDecimals, decimalSeparator, thousandsSeparator)
}

export function formatDateForPost(date, customFormat) {
  if (!date) {
    return null
  }
  let matchesBackedDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
  return matchesBackedDate
    ? date
    : customFormat
    ? moment(date, customFormat).format('YYYY-MM-DD')
    : moment(date).format('YYYY-MM-DD')
}

export function formatDateForLocale(date) {
  if (!date) {
    return
  }
  let matchesBackedDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
  let matchesDisplayDate = /^\d{2}\/\d{2}\/\d{4}$/.test(date)
  // format date based on locale
  let result = null
  if (matchesBackedDate) result = moment(date, 'YYYY-MM-DD').format(store.state.settings.date_format)
  if (matchesDisplayDate) result = date
  return result
}

export function formatDateForCustomDisplay(date, format) {
  if (!format) {
    format = store.state.settings.date_format
  }
  return moment(date).format(format)
}

export function formatDateTimeForLocale(date, locale) {
  if (!locale) {
    locale = store.state.settings.locale
  }
  moment.locale(locale)
  let utc = momentTz.tz(date, 'UTC')
  let localMoment = utc.clone().tz(store.state.settings.timezone)
  return localMoment.format(`${store.state.settings.date_format} LT`)
}

export function getNumericCellEditor(locale, decimalSeparator, thousandSeparator) {
  // function to act as a class
  function NumericCellEditor() {}

  // gets called once before the renderer is used
  NumericCellEditor.prototype.init = function(params) {
    // set output decimals
    this.outputDecimals = params.decimals || 2
    // create the cell
    this.eInput = document.createElement('input')
    this.eInput.setAttribute('type', 'text')
    this.eInput.classList.add('ag-cell-edit-input')
    this.eInput.value = params.value
      ? formatPrice(params.value, this.outputDecimals, false, locale, decimalSeparator, thousandSeparator)
      : ''
  }

  // gets called once when grid ready to insert the element
  NumericCellEditor.prototype.getGui = function() {
    return this.eInput
  }

  // focus and select can be done after the gui is attached
  NumericCellEditor.prototype.afterGuiAttached = function() {
    this.eInput.focus()
    this.eInput.select()
  }

  // returns the new value after editing
  NumericCellEditor.prototype.getValue = function() {
    return convertToNumber(
      formatInputNumber(this.eInput.value, this.outputDecimals, decimalSeparator, thousandSeparator),
      this.outputDecimals
    )
  }

  // any cleanup we need to be done here
  NumericCellEditor.prototype.destroy = function() {
    // but this example is simple, no cleanup, we could
    // even leave this method out as it's optional
  }

  // if true, then this editor will appear in a popup
  NumericCellEditor.prototype.isPopup = function() {
    // and we could leave this method out also, false is the default
    return false
  }
  return NumericCellEditor
}

export function getDateCellEditor(dateFormat) {
  // function to act as a class
  function DateCellEditor() {}

  // gets called once before the renderer is used
  DateCellEditor.prototype.init = function(params) {
    // create the cell
    this.eInput = document.createElement('input')
    this.eInput.setAttribute('type', 'text')
    this.eInput.classList.add('ag-cell-edit-input')
    this.eInput.value = params.value ? moment(params.value).format(dateFormat) : ''
    this.eInput.placeholder = store.state.settings.date_format
  }

  // gets called once when grid ready to insert the element
  DateCellEditor.prototype.getGui = function() {
    return this.eInput
  }

  // focus and select can be done after the gui is attached
  DateCellEditor.prototype.afterGuiAttached = function() {
    this.eInput.focus()
    this.eInput.select()
  }

  // returns the new value after editing
  DateCellEditor.prototype.getValue = function() {
    return this.eInput.value
  }

  // any cleanup we need to be done here
  DateCellEditor.prototype.destroy = function() {
    // but this example is simple, no cleanup, we could
    // even leave this method out as it's optional
  }

  // if true, then this editor will appear in a popup
  DateCellEditor.prototype.isPopup = function() {
    // and we could leave this method out also, false is the default
    return false
  }

  return DateCellEditor
}
